import AddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../context/CaseContext";
import { NamedValue, Party } from "../shared/CaseModel";
import randomId from "../shared/randomId";
import { PartyDetails } from "./PartyDetails";
import { PartyEditorDialog } from "./PartyEditorDialog";
import CenteredMessageBox from "./general/CenteredMessageBox";
import { SectionLabel } from "./general/SectionLabel";
import { GuideButton } from "./guide/GuideButton";
import { GuideMarkers } from "./guide/GuideContext";
import { GuideMarker } from "./guide/GuideMarker";

export interface PartyCallbacks {
  setName: (party: Party, newName: string) => void;
  setClient: (party: Party) => void;
  setNonClient: (party: Party) => void;
  setAssets: (party: Party, assets: NamedValue[] | "unlimited") => void;
  add: (party: Party) => void;
  delete: (party: Party) => void;
}

export const Parties = () => {
  const caseContext = useCase();
  const submit = useCaseSubmit();
  const updateParty = (party: Party) =>
    submit({ party: JSON.stringify(party) }, { method: "put" });

  const partyCallbacks: PartyCallbacks = {
    setName: function (party: Party, name: string): void {
      updateParty({ ...party, name });
    },
    setClient: function (party: Party): void {
      updateParty({ ...party, client: true });
    },
    setNonClient: function (party: Party): void {
      updateParty({ ...party, client: false });
    },
    setAssets: function (
      party: Party,
      assets: NamedValue[] | "unlimited",
    ): void {
      updateParty({ ...party, assets });
    },
    add: function (party: Party): void {
      submit(
        {
          partyId: party.id,
          name: party.name,
          client: party.client ? "true" : "false",
        },
        { method: "post" },
      );
    },
    delete: (party) => {
      submit({ partyId: party.id }, { method: "delete" });
    },
  };

  return (
    <PartiesComponent
      parties={caseContext.getCase().parties || []}
      partyCallbacks={partyCallbacks}
    />
  );
};

export const PartiesComponent = ({
  parties,
  partyCallbacks,
}: {
  parties: Party[];
  partyCallbacks: PartyCallbacks;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.parties" });
  const { readOnly } = useCase();
  const [newParty, setNewParty] = useState<Party>();
  const startNewParty = () => setNewParty({ id: randomId(), name: `` });

  const clientParties = parties.filter((party) => party.client);
  const nonClientParties = parties.filter((party) => !party.client);
  // the guide marks what the parties have in common on the first one
  const first = clientParties[0] ?? nonClientParties[0];

  const renderPartyList = (list: Party[], label: string) => {
    if (list.length === 0) return null;

    return (
      <Stack component="section" spacing={1.25}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <SectionLabel component="h2" rule>
              {label}
            </SectionLabel>
          </Box>
          {list.includes(first) && <GuideMarker entry="client" />}
        </Stack>
        {list.map((party) => (
          // by id: what a card holds open belongs to its party, also once a
          // party above it is gone
          <GuideMarkers key={party.id} shown={party === first}>
            <PartyDetails party={party} partyCallbacks={partyCallbacks} />
          </GuideMarkers>
        ))}
      </Stack>
    );
  };

  return (
    <Stack spacing={2.75}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "70ch" }}
          >
            {t("description")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <GuideButton />
          {/* without parties the empty state below carries this button */}
          {parties.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={readOnly}
              onClick={startNewParty}
              title={t("tip.add")}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("add")}
            </Button>
          )}
        </Stack>
      </Stack>

      {parties.length === 0 ? (
        <CenteredMessageBox
          message={t("noParties")}
          action={{
            label: t("add"),
            icon: <AddIcon />,
            onClick: startNewParty,
            disabled: readOnly,
          }}
        />
      ) : (
        <>
          {renderPartyList(clientParties, t("section.client"))}
          {renderPartyList(nonClientParties, t("section.noClient"))}
        </>
      )}

      {newParty && (
        <PartyEditorDialog
          party={newParty}
          onClose={(party) => {
            if (party) partyCallbacks.add(party);
            setNewParty(undefined);
          }}
          open={newParty !== undefined}
        />
      )}
    </Stack>
  );
};
